import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Combobox } from '@/components/ui/combobox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, Pencil, Trash2, Calendar as CalendarIcon, LayoutGrid, List, CalendarDays } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import AppointmentCalendar from '@/components/AppointmentCalendar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  PREVU: 'bg-blue-100 text-blue-800',
  REALISE: 'bg-green-100 text-green-800',
  ANNULE: 'bg-red-100 text-red-800',
  NO_SHOW: 'bg-gray-100 text-gray-800',
};

export default function Appointments() {
  const { getAuthHeaders } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [calendarView, setCalendarView] = useState('month');
  const [open, setOpen] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [filters, setFilters] = useState({
    clientId: 'all',
    statut: 'all',
    dateFrom: '',
    dateTo: '',
  });
  const [formData, setFormData] = useState({
    client_id: '',
    service_id: '',
    date: '',
    statut: 'PREVU',
    prix: '',
  });

  const fetchData = async () => {
    try {
      const [aptsRes, clientsRes, servicesRes] = await Promise.all([
        axios.get(`${API_URL}/api/appointments`, getAuthHeaders()),
        axios.get(`${API_URL}/api/clients`, getAuthHeaders()),
        axios.get(`${API_URL}/api/services`, getAuthHeaders()),
      ]);
      const sorted = aptsRes.data.sort((a, b) => new Date(b.date) - new Date(a.date));
      setAppointments(sorted);
      setFilteredAppointments(sorted);
      setClients(clientsRes.data);
      setServices(servicesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    let filtered = [...appointments];

    if (filters.clientId && filters.clientId !== 'all') {
      filtered = filtered.filter(apt => apt.client_id === filters.clientId);
    }

    if (filters.statut && filters.statut !== 'all') {
      filtered = filtered.filter(apt => apt.statut === filters.statut);
    }

    if (filters.dateFrom) {
      filtered = filtered.filter(apt => new Date(apt.date) >= new Date(filters.dateFrom));
    }

    if (filters.dateTo) {
      filtered = filtered.filter(apt => new Date(apt.date) <= new Date(filters.dateTo));
    }

    setFilteredAppointments(filtered);
  }, [filters, appointments]);

  const handleServiceChange = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    setFormData({
      ...formData,
      service_id: serviceId,
      prix: service ? service.prix.toString() : '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        prix: parseFloat(formData.prix),
      };
      if (editingAppointment) {
        await axios.put(`${API_URL}/api/appointments/${editingAppointment.id}`, payload, getAuthHeaders());
        toast.success('Rendez-vous modifié');
      } else {
        await axios.post(`${API_URL}/api/appointments`, payload, getAuthHeaders());
        toast.success('Rendez-vous créé');
      }
      fetchData();
      handleClose();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/appointments/${deleteId}`, getAuthHeaders());
      toast.success('Rendez-vous supprimé');
      fetchData();
      setDeleteId(null);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEdit = (appointment) => {
    setEditingAppointment(appointment);
    setFormData({
      client_id: appointment.client_id,
      service_id: appointment.service_id,
      date: appointment.date.slice(0, 16),
      statut: appointment.statut,
      prix: appointment.prix.toString(),
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingAppointment(null);
    setFormData({ client_id: '', service_id: '', date: '', statut: 'PREVU', prix: '' });
  };

  const handleAppointmentDrop = async (apt, newDateStr) => {
    try {
      const payload = {
        client_id: apt.client_id,
        service_id: apt.service_id,
        date: newDateStr,
        statut: apt.statut,
        prix: apt.prix,
      };
      await axios.put(`${API_URL}/api/appointments/${apt.id}`, payload, getAuthHeaders());
      toast.success('Rendez-vous reprogrammé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la reprogrammation');
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? `${client.prenom} ${client.nom}` : 'Client inconnu';
  };

  const getServiceName = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    return service ? service.nom : 'Service inconnu';
  };

  return (
    <div className="space-y-8" data-testid="appointments-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Rendez-vous</h1>
          <p className="text-muted-foreground mt-2">Planifiez et suivez vos rendez-vous</p>
        </div>
        <div className="flex flex-wrap items-center gap-3 sm:gap-4">
          <ToggleGroup type="single" value={viewMode} onValueChange={(v) => v && setViewMode(v)} data-testid="view-toggle">
            <ToggleGroupItem value="grid" aria-label="Vue grille">
              <LayoutGrid className="w-4 h-4" />
            </ToggleGroupItem>
            <ToggleGroupItem value="list" aria-label="Vue liste">
              <List className="w-4 h-4" />
            </ToggleGroupItem>
            <ToggleGroupItem value="calendar" aria-label="Vue calendrier">
              <CalendarDays className="w-4 h-4" />
            </ToggleGroupItem>
          </ToggleGroup>
          {viewMode === 'calendar' && (
            <Select value={calendarView} onValueChange={setCalendarView}>
              <SelectTrigger className="w-[120px]" data-testid="calendar-view-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="month">Mois</SelectItem>
                <SelectItem value="week">Semaine</SelectItem>
              </SelectContent>
            </Select>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="btn-add-appointment">
                <Plus className="w-4 h-4 mr-2" />
                Nouveau rendez-vous
              </Button>
            </DialogTrigger>
          <DialogContent data-testid="appointment-dialog">
            <DialogHeader>
              <DialogTitle>{editingAppointment ? 'Modifier' : 'Nouveau'} rendez-vous</DialogTitle>
              <DialogDescription>
                Planifiez un rendez-vous
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="client_id">Client·e *</Label>
                <Combobox
                  options={clients.map(client => ({
                    value: client.id,
                    label: `${client.prenom} ${client.nom}`,
                    searchValue: `${client.prenom} ${client.nom}`,
                  }))}
                  value={formData.client_id}
                  onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                  placeholder="Sélectionnez un·e client·e"
                  searchPlaceholder="Rechercher un·e client·e..."
                  emptyText="Aucun·e client·e trouvé·e"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="service_id">Prestation *</Label>
                <Combobox
                  options={services.map(service => ({
                    value: service.id,
                    label: `${service.nom} - ${service.prix}€`,
                    searchValue: service.nom,
                  }))}
                  value={formData.service_id}
                  onValueChange={handleServiceChange}
                  placeholder="Sélectionnez une prestation"
                  searchPlaceholder="Rechercher une prestation..."
                  emptyText="Aucune prestation trouvée"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="date">Date et heure *</Label>
                <Input
                  id="date"
                  type="datetime-local"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  data-testid="input-date"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="statut">Statut *</Label>
                <Select value={formData.statut} onValueChange={(value) => setFormData({ ...formData, statut: value })}>
                  <SelectTrigger data-testid="select-statut">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PREVU">Prévu</SelectItem>
                    <SelectItem value="REALISE">Réalisé</SelectItem>
                    <SelectItem value="ANNULE">Annulé</SelectItem>
                    <SelectItem value="NO_SHOW">No-show</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="prix">Prix (€) *</Label>
                <Input
                  id="prix"
                  type="number"
                  step="0.01"
                  value={formData.prix}
                  onChange={(e) => setFormData({ ...formData, prix: e.target.value })}
                  required
                  data-testid="input-prix"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleClose}>Annuler</Button>
                <Button type="submit" data-testid="btn-submit-appointment">Enregistrer</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card className="rounded-xl border-border/60" data-testid="filters-card">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="filter-client">Client·e</Label>
              <Combobox
                options={[
                  { value: 'all', label: 'Tou·te·s les client·e·s' },
                  ...clients.map(c => ({ value: c.id, label: `${c.prenom} ${c.nom}` }))
                ]}
                value={filters.clientId}
                onValueChange={(value) => setFilters({ ...filters, clientId: value || 'all' })}
                placeholder="Tou·te·s les client·e·s"
                searchPlaceholder="Rechercher..."
                emptyText="Aucun·e client·e trouvé·e"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="filter-statut">Statut</Label>
              <Select value={filters.statut} onValueChange={(value) => setFilters({ ...filters, statut: value })}>
                <SelectTrigger data-testid="filter-statut">
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="PREVU">Prévu</SelectItem>
                  <SelectItem value="REALISE">Réalisé</SelectItem>
                  <SelectItem value="ANNULE">Annulé</SelectItem>
                  <SelectItem value="NO_SHOW">No-show</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="filter-date-from">Date début</Label>
              <Input
                id="filter-date-from"
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                data-testid="filter-date-from"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="filter-date-to">Date fin</Label>
              <Input
                id="filter-date-to"
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                data-testid="filter-date-to"
              />
            </div>
          </div>
          {(filters.clientId !== 'all' || filters.statut !== 'all' || filters.dateFrom || filters.dateTo) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFilters({ clientId: 'all', statut: 'all', dateFrom: '', dateTo: '' })}
              className="mt-4"
              data-testid="btn-clear-filters"
            >
              Réinitialiser les filtres
            </Button>
          )}
        </CardContent>
      </Card>

      {filteredAppointments.length === 0 && appointments.length > 0 ? (
        <Card className="rounded-xl border-border/60">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CalendarIcon className="w-16 h-16 text-muted-foreground mb-4" strokeWidth={1} />
            <p className="text-muted-foreground">Aucun rendez-vous ne correspond aux filtres</p>
          </CardContent>
        </Card>
      ) : filteredAppointments.length === 0 ? (
        <Card className="rounded-xl border-border/60">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CalendarIcon className="w-16 h-16 text-muted-foreground mb-4" strokeWidth={1} />
            <p className="text-muted-foreground">Aucun rendez-vous pour le moment</p>
          </CardContent>
        </Card>
      ) : viewMode === 'calendar' ? (
        <AppointmentCalendar
          appointments={appointments}
          clients={clients}
          services={services}
          view={calendarView}
          onAppointmentClick={handleEdit}
          onAppointmentDrop={handleAppointmentDrop}
        />
      ) : viewMode === 'list' ? (
        <Card className="rounded-xl border-border/60">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client·e</TableHead>
                <TableHead>Prestation</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Prix</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAppointments.map((apt) => (
                <TableRow key={apt.id} data-testid={`appointment-row-${apt.id}`}>
                  <TableCell className="font-medium">{getClientName(apt.client_id)}</TableCell>
                  <TableCell>{getServiceName(apt.service_id)}</TableCell>
                  <TableCell>{format(new Date(apt.date), 'dd/MM/yyyy HH:mm')}</TableCell>
                  <TableCell><Badge className={statusColors[apt.statut]}>{apt.statut}</Badge></TableCell>
                  <TableCell className="font-semibold text-primary">{apt.prix.toFixed(2)} €</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(apt)} data-testid={`btn-edit-${apt.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteId(apt.id)} className="text-destructive hover:text-destructive" data-testid={`btn-delete-${apt.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredAppointments.map((apt) => (
            <Card
              key={apt.id}
              className="rounded-xl border-border/60 hover:border-primary/20 transition-colors"
              data-testid={`appointment-card-${apt.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold">{getClientName(apt.client_id)}</h3>
                      <Badge className={statusColors[apt.statut]}>{apt.statut}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{getServiceName(apt.service_id)}</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {format(new Date(apt.date), 'PPP à HH:mm', { locale: fr })}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-primary">{apt.prix.toFixed(2)} €</p>
                    <div className="flex gap-2 mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(apt)}
                        data-testid={`btn-edit-${apt.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteId(apt.id)}
                        className="text-destructive hover:text-destructive"
                        data-testid={`btn-delete-${apt.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Êtes-vous sûr ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le rendez-vous sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="btn-cancel-delete">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="btn-confirm-delete">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}